import { CURRENCY_SIGNS, DEFAULT_LOCALE } from '@/types'
import type { Invoice } from '@/types'

interface TotalsBlockProps {
	invoice: Invoice
}

function formatMoney(value: number, currency: string): string {
	const sign = CURRENCY_SIGNS[currency] ?? currency
	return `${value.toLocaleString(DEFAULT_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${sign}`
}

export function TotalsBlock({ invoice }: TotalsBlockProps) {
	const total = Number(invoice.amount)
	const items = invoice.items ?? []

	// The figures are frozen on the invoice when it is issued, so they are read
	// rather than recomputed. Recomputing here would also disagree with the
	// server the moment a rental's rate is edited — which is the defect this
	// replaces.
	const vatAmount = invoice.vatAmount != null ? Number(invoice.vatAmount) : undefined

	// "0,00 ₽" and "Без НДС" are different statements: the first claims VAT was
	// charged and came to nothing, the second that the supply carries none.
	// Older invoices have neither figure and show a dash.
	const vatLine =
		vatAmount != null && vatAmount > 0
			? formatMoney(vatAmount, invoice.currency)
			: invoice.vatClause ?? (vatAmount != null ? 'Без НДС' : '—')

	return (
		<div className="flex flex-col gap-1 text-xs">
			<div className="flex justify-end gap-12">
				<span className="text-(--color-muted-foreground)">Итого:</span>
				<span className="min-w-[120px] text-right font-[600]">{formatMoney(total, invoice.currency)}</span>
			</div>
			<div className="flex justify-end gap-12">
				<span className="text-(--color-muted-foreground)">В том числе НДС:</span>
				<span className="min-w-[120px] text-right">{vatLine}</span>
			</div>
			<div className="flex justify-end gap-12">
				<span className="text-(--color-muted-foreground)">Всего к оплате:</span>
				<span className="min-w-[120px] text-right font-[600]">{formatMoney(total, invoice.currency)}</span>
			</div>
			<p className="mt-3">
				Всего наименований {items.length}, на сумму {formatMoney(total, invoice.currency)}
			</p>
			{invoice.amountInWords && (
				<p className="font-[600]">{invoice.amountInWords}</p>
			)}
		</div>
	)
}
